import { useContext, useEffect, useRef, useState } from 'react';
import './chat.scss';
import { AuthContext } from '../../context/AuthContext';
import apiRequest from '../../lib/apiRequest';
import { format } from 'timeago.js';
import { SocketContext } from '../../context/SocketContext';
import { useNotificationStore } from '../../lib/notificationStore';
import Loader from '../loader/Loader';

const SingleChat = ({ chatId, post }) => {
  const [chatData, setChatData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasError, setHasError] = useState(false);
  const messageEndRef = useRef();

  const { currentUser } = useContext(AuthContext);
  const { socket } = useContext(SocketContext);
  const decrease = useNotificationStore((state) => state.decrease);

  const fetchChat = async () => {
    if (!chatId) return;
    try {
      setIsLoading(true);
      const res = await apiRequest.get(`/chats/${chatId}`);
      const data = res.data;

      // 💥 Защита от пустого ответа
      if (!data || !data.messages || !data.receiver) {
        setHasError(true);
        return;
      }

      setChatData(data);
      await apiRequest.put(`/chats/read/${chatId}`);
      decrease();
    } catch (err) {
      console.error('❌ Ошибка загрузки чата:', err);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChat();
  }, [chatId]);

  useEffect(() => {
    if (chatData?.messages?.length) {
      messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatData?.messages?.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || !chatData?.receiver) return;

    const text = new FormData(e.target).get('text');
    if (!text) return;

    try {
      setSending(true);
      const res = await apiRequest.post(`/messages/${chatId}`, { text });
      setChatData((prev) => ({
        ...prev,
        messages: [...prev.messages, res.data],
      }));
      e.target.reset();

      socket?.emit('sendMessage', {
        receiverId: chatData.receiver.id,
        data: res.data,
      });
    } catch (err) {
      console.error('❌ Ошибка отправки сообщения:', err);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handleIncoming = async (data) => {
      if (data.chatId !== chatId) return;
      setChatData((prev) => ({
        ...prev,
        messages: [...prev.messages, data],
      }));
      try {
        await apiRequest.put(`/chats/read/${chatId}`);
        decrease();
      } catch (err) {
        console.error('❌ Ошибка при отметке прочтения:', err);
      }
    };

    socket.on('getMessage', handleIncoming);
    return () => socket.off('getMessage', handleIncoming);
  }, [socket, chatId]);

  const avatar = post?.images?.[0]?.url || '/noproduct.webp';

  // 🔎 Отображение компонентов по состоянию
  return (
    <div className="singleChat">
      <div className="singleChatBox">
        <div className="bstop">
          <div className="bsuser">
            <a href={`/${post?.id}`} target="_blank" rel="noopener noreferrer">
              <img
                src={avatar}
                alt="Post Avatar"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/noproduct.webp';
                }}
              />
            </a>
            {chatData?.receiver?.username || '...'}
          </div>
        </div>

        <div className="singleMessages">
          {isLoading ? (
            <Loader />
          ) : hasError ? (
            <p className="error">Can't load the chat</p>
          ) : (
            <>
              {chatData.messages.map((message) => (
                <div
                  className={`chatMessage ${
                    message.userId === currentUser?.user?.id
                      ? 'sent'
                      : 'received'
                  }`}
                  key={message.id}
                >
                  <p>{message.text}</p>
                  <span>{format(message.createdAt, 'dd/MM/yyyy HH:mm')}</span>
                </div>
              ))}
              <div ref={messageEndRef}></div>
            </>
          )}
        </div>

        {!isLoading && !hasError && (
          <form onSubmit={handleSubmit} className="bschatInput">
            <textarea
              name="text"
              placeholder="Type a message..."
              required
              disabled={sending}
            ></textarea>
            <button type="submit" disabled={sending}>
              {sending ? 'Sending...' : 'Send'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SingleChat;
